'use client'
/* eslint-disable @typescript-eslint/no-explicit-any */
import { PortableText, PortableTextProps } from '@portabletext/react'
import {
  OrSecondaryNavbarBdp,
  MlImageGrid,
  Image,
  AtLink,
  AtLinkType,
  SpaceType,
  AtLinkProps,
  Media,
} from '@studio/atomic-ui'
import { SanityAtLink, SanitySvgAsset } from '../../services/sanity/types'
import { ContentBlock } from './content-block'
import { MouseEvent, useRef } from 'react'
import { trackNavigationMenuClick } from '../../utils/analytics'
import { useShowcasePathInfo } from '../../hooks/useShowcasePathInfo'

export interface SanityAssetList {
  items: Array<{ icon: SanitySvgAsset; title: string; body?: string }>
}

export interface BuildingDetailTemplate {
  pageLinks: SanityAtLink[]
  buildingImages: Image[]
  address: {
    heading: PortableTextProps['value']
    addressInfo: PortableTextProps['value']
    directionLink?: AtLinkProps
    mapTile: {
      address: string
      details: SanityAssetList
    }
  }
  content: Array<ContentBlock>
  spacesSection: {
    spaceType: SpaceType
    allSpacesButton: {
      actionType: string
      actionValue: string
      label: string
    }
  }
  building: string
  isNonStudioLocation?: boolean
}

export interface TmBuildingDetailProps {
  template: BuildingDetailTemplate
  title: string
}

export const TmBuildingDetail = ({ template, title = '' }: TmBuildingDetailProps) => {
  const { type, types, region, building, capacity } = useShowcasePathInfo({ useProductParam: true })
  const filterBlock = template.content?.find((item) => item.layout === 'longText' || item.fieldName === 'longText')
  if (filterBlock) {
    if (type) {
      filterBlock.filterType = type
    }
    filterBlock.filterTypes = types
    filterBlock.filterRegion = region
    filterBlock.filterBuilding = building
    filterBlock.filterCapacity = capacity
  }
  const observerRefs = useRef({
    overview: null,
    'getting-here': null,
    'book-a-space': null,
    'building-features': null,
    'workspace-options': null,
  })
  const addressInfo = {
    ...(template.address.addressInfo as any)[0],
    children: [
      {
        ...(template.address.addressInfo as any)[0].children[1],
        marks: [],
      },
    ],
  }

  return (
    <div className="lg:mx-auto">
      <section className="flex justify-center">
        <div className="mt-12">
          <MlImageGrid media={template.buildingImages as Media[]} maxVisible={4} large={true} />
        </div>
      </section>
      <section className="lg:mt-12 lg:mb-12 mt-6 mb-8 lg:mx-auto lg:max-w-screen-lg lg:px-[10.4375rem]">
        <div className="flex px-4 md:px-30 lg:px-0 w-fullgap-16 flex-wrap">
          <div className="lg:w-6/12 w-full">
            <div className="flex flex-col gap-4 md:gap-12 md:flex-row mt-4 justify-start">
              <h2 className="text-[3.5rem] font-medium font-heading">{title}</h2>
            </div>
          </div>
          <div className="lg:w-6/12 w-full">
            {template.address && addressInfo && (
              <div className="flex flex-col gap-4 md:gap-12 md:flex-row lg:ml-24 lg:mt-4 justify-between">
                <PortableText
                  value={addressInfo}
                  components={{
                    block: {
                      normal: ({ children }) => <p className="text-neutrals-800 text-14 lg:text-16">{children}</p>,
                      strong: ({ children }) => (
                        <strong className="only:inline-block first:inline-block first:mt-6 only:mt-6 font-bold text-neutrals-800 leading-1.5">
                          {children}
                        </strong>
                      ),
                    },
                  }}
                />
                {template.address.directionLink && (
                  <div className="flex lg:justify-end justify-start">
                    <AtLink
                      type={AtLinkType.DEFAULT}
                      {...template.address.directionLink}
                      className="text-14 lg:text-16 w-auto"
                      classIconRight="w-4 ml-4"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <OrSecondaryNavbarBdp
        observerRefs={observerRefs}
        links={template.pageLinks.map((link) => {
          return {
            ...link,
            onTrackClick: (event?: MouseEvent<HTMLAnchorElement>) => {
              event?.stopPropagation()
              trackNavigationMenuClick({
                menuTitle: link.label?.toLocaleLowerCase() || '',
                menuHeading: link.label?.toLocaleLowerCase() || '',
                menuType: 'secondary navigation',
                linkUrl: link.href || '',
                componentName: 'secondary nav bar bdp',
                windowLocationHref: window.location.href,
              })
            },
          }
        })}
        accountForNavbar
      />

      {template.content?.map((item, index) => (
        <ContentBlock key={index} {...template} content={item} observerRefs={observerRefs} />
      ))}
    </div>
  )
}
